import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { useTranslation } from '@/hooks/useTranslation';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Notification item structure
export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read: boolean;
  created_at: string;
  avatar_url?: string;
  is_invitation?: boolean;
  invitation_data?: {
    company_name: string;
    company_logo: string | null;
    member_role: string;
    invited_by: string;
  };
}

// Context value interface
interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  loadNotifications: (limit?: number) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

// Create context
const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// Provider props
interface NotificationProviderProps {
  children: ReactNode;
}

// Singleton real-time channel management (shared across all hook instances)
let realtimeChannel: RealtimeChannel | null = null;
let subscriberCount = 0;
let isCleaningUp = false; // Prevent concurrent cleanup race conditions

// Provider component
export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const toast = useTranslatedToast();
  const { t } = useTranslation('toast');
  const loadingRef = useRef(false);

  // Debug: Track mount/unmount
  useEffect(() => {
    console.log('🔵 [NotificationProvider] MOUNTED');
    return () => {
      console.log('🔴 [NotificationProvider] UNMOUNTED');
    };
  }, []);

  // Get appropriate toast method based on notification urgency
  const getToastMethod = useCallback((type: string, data: any) => {
    const urgency = data?.urgency || 'medium';
    
    switch (urgency) {
      case 'critical':
        return toast.error;
      case 'high':
        return toast.warning;
      case 'low':
        return toast.info;
      default:
        return toast.success;
    }
  }, [toast]);

  // Load notifications from database
  const loadNotifications = useCallback(async (limit = 50) => {
    // Prevent concurrent loads
    if (loadingRef.current) {
      console.log('⏳ Notification load already in progress, skipping...');
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No user found, skipping notification load');
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      // Fetch notifications
      const { data: rawNotifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error loading notifications:', error);
        throw error;
      }

      // Process notifications to identify actionable invitations
      const processedNotifications = ((rawNotifications as any[]) || []).map((notification: any) => {
        const isInvitation = notification.type === 'company_invitation';
        
        if (isInvitation && notification.data) {
          const data = notification.data as any;
          return {
            ...notification,
            is_invitation: true,
            invitation_data: {
              company_name: data.company_name || 'Unknown Company',
              company_logo: data.company_logo || null,
              member_role: data.member_role || 'member',
              invited_by: data.invited_by || 'Someone'
            }
          };
        }
        
        return notification;
      });

      setNotifications(processedNotifications);
      setUnreadCount(processedNotifications.filter(notif => !notif.read).length);

      // Show single summary toast for unread notifications (ONLY ONCE per session per user)
      const sessionKey = `notif.unread_summary_shown.${user.id}`;
      const hasShownToast = sessionStorage.getItem(sessionKey) === 'true';
      
      if (!hasShownToast && processedNotifications.length > 0) {
        const unreadCount = processedNotifications.filter(n => !n.read).length;
        
        if (unreadCount > 0) {
          // Set guard BEFORE showing toast to prevent race conditions
          sessionStorage.setItem(sessionKey, 'true');
          
          toast.info('notifications.unread_summary', {
            description: t('notifications.unread_description', { count: unreadCount }),
            duration: 5000
          });
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [toast]);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Update in database
      const { error } = await supabase
        .from('notifications')
        .update({ read: true } as any)
        .eq('id', notificationId as any);

      if (error) {
        console.error('Error marking notification as read:', error);
        // Revert optimistic update on error
        await loadNotifications();
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      await loadNotifications();
    }
  }, [loadNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);

      // Update in database
      const { error } = await supabase
        .from('notifications')
        .update({ read: true } as any)
        .eq('user_id', user.id as any)
        .eq('read', false as any);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        // Revert optimistic update on error
        await loadNotifications();
      } else {
        toast.success('notifications.all_marked_read');
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      await loadNotifications();
    }
  }, [toast, loadNotifications]);

  // Setup real-time subscription (SINGLETON - only one subscription for entire app)
  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No user, skipping notification subscription');
        return;
      }

      subscriberCount++;
      console.log(`📡 Notification subscriber count: ${subscriberCount}`);

      // Only create channel if it doesn't exist
      if (!realtimeChannel) {
        console.log('🆕 Creating new notification real-time channel');
        realtimeChannel = supabase
          .channel('notifications-realtime')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              console.log('📬 New notification received:', payload.new);
              loadNotifications();
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              console.log('🔄 Notification updated:', payload.new);
              loadNotifications();
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'DELETE',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              console.log('🗑️ Notification deleted:', payload.old);
              loadNotifications();
            }
          )
          .subscribe();
      } else {
        console.log('♻️ Reusing existing notification real-time channel');
      }
    };

    setupRealtimeSubscription();

    return () => {
      subscriberCount = Math.max(0, subscriberCount - 1); // Prevent negative
      console.log(`📡 [SafeCleanup] Notification subscriber count: ${subscriberCount}`);
      
      // Only remove channel when no subscribers remain and not already cleaning up
      if (subscriberCount <= 0 && realtimeChannel && !isCleaningUp) {
        isCleaningUp = true;
        console.log('🔌 [SafeCleanup] Removing notification channel...');
        supabase.removeChannel(realtimeChannel)
          .then(() => {
            realtimeChannel = null;
            isCleaningUp = false;
            console.log('✅ [SafeCleanup] Channel removed successfully');
          })
          .catch((err) => {
            console.error('❌ [SafeCleanup] Error removing channel:', err);
            realtimeChannel = null; // Clear anyway
            isCleaningUp = false;
          });
      }
    };
  }, [loadNotifications]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Clear sessionStorage guard on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        console.log('🔓 User signed out, clearing notification toast guards');
        // Clear all notification summary guards
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('notif.unread_summary_shown.')) {
            sessionStorage.removeItem(key);
          }
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Hook to use notification context
export function useNotificationContext() {
  const context = useContext(NotificationContext);
  
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  
  return context;
}
